<template>
  <error-boundary module="document-module">
    <hr-module class="document-module" mobile-menu no-header>
      <template v-slot:left>
        <hr-steps
          @step-changed="autoSave"
          :current-document="state.currentDocument"
          @review-and-submit="reviewAndSubmit"
        ></hr-steps>
      </template>

      <hr-module-header
        v-if="!isSuperApiDocument"
        :title="state.currentDocument.title"
        sub-module
      >
        <p v-html="state.currentDocument.description"></p>
      </hr-module-header>

      <div v-if="isAnySignatureLoading" class="signature-loading-overlay">
        <hr-loader-block />
      </div>

      <div v-if="state.errorMessageArray.length" class="alert alert-danger">
        <div
          v-for="(errorMessage, index) in state.errorMessageArray"
          :key="index"
        >
          {{ errorMessage }}
        </div>
      </div>
      <hr-super-api v-if="isSuperApiDocument" class="document-module__field" />
      <div v-else v-show="!isAnySignatureLoading">
        <template
          v-for="(field, index) in state.currentDocument.fields"
          :key="index"
        >
          <hr-au-rtw
            v-if="
              getDocumentType(field) === DOCUMENT_NAMES.WORK_RIGHTS &&
              store.state.onboarding.countryCode === COUNTRY.AU
            "
            v-model="state.currentDocument.fields[index]"
            class="document-module__field"
            :key="`${state.currentDocument.name}-au-rtw-${index}`"
            @update:model-value="(val) => fieldChange(index, val)"
            @update="(val) => updateUpload(index, val)"
            @focus="(child) => fieldFocus(child || field)"
            :data-test-id="`hr-${state.currentDocument.name.toLowerCase()}-${index}`"
          />
          <hr-field-upload
            v-else-if="field.type === 'FILE_UPLOAD'"
            :key="`${state.currentDocument.name}-upload-${index}`"
            :error-message="field.fieldError"
            :model-value="state.currentDocument.fields[index]"
            class="document-module__field __upload"
            @update="(val) => updateUpload(index, val)"
          />
          <hr-field-signature
            v-else-if="field.type === 'DOCSIGNATURE_REQUEST'"
            :key="`${state.currentDocument.name}-signature-${index}`"
            v-model="state.currentDocument.fields[index]"
            class="document-module__field"
            @input="(val) => fieldChange(index, val)"
            @save="save"
            @loading-start="handleSignatureLoadingStatus(index, true)"
            @loading-end="handleSignatureLoadingStatus(index, false)"
          />
          <hr-question-factory
            v-else-if="field.type === 'CUSTOM_QUESTION'"
            v-model="state.currentDocument.fields[index]"
            class="document-module__field"
            :key="`${state.currentDocument.name}-question-${index}`"
            @update:model-value="(val) => fieldChange(index, val)"
            :data-test-id="`hr-${state.currentDocument.name.toLowerCase()}-${index}`"
          >
          </hr-question-factory>
          <hr-tfn
            v-else-if="
              getDocumentType(field) === DOCUMENT_NAMES.TFN_DECLARATION
            "
            v-model="state.currentDocument.fields[index]"
            class="document-module__field"
            :key="`${state.currentDocument.name}-tfn-${index}`"
            @update:model-value="(val) => fieldChange(index, val)"
            @focus="(child) => fieldFocus(child || field)"
            :data-test-id="`hr-${state.currentDocument.name.toLowerCase()}-${index}`"
          >
          </hr-tfn>
          <h-r-field
            v-else
            :key="`${state.currentDocument.name}-field-${index}`"
            v-model="state.currentDocument.fields[index]"
            @update:model-value="(val) => fieldChange(index, val)"
            @focus="(child) => fieldFocus(child || field)"
          ></h-r-field>
        </template>
      </div>

      <hr-document-module-info :document="state.currentDocument" />

      <hr-button-save
        v-if="!isSuperApiDocument"
        :class="{ hasFooter: isDocumentsComplete }"
        :state="state.saveState"
        class="col-xs-6 col-sm-12"
        @click="save"
      />
    </hr-module>
  </error-boundary>
</template>

<script>
import { reactive, computed, watch, onUpdated, onMounted, nextTick } from 'vue';
import store from '@/store';
import cloneObj from '@/core/cloneObj';
import HrSteps from '@/components/hr-steps.vue';
import HRField from '@/components/HRField';
import HrButtonSave from '@/components/hr-button-save.vue';
import HrModuleHeader from '@/components/hr-module-header.vue';
import HrModule from '@/components/hr-module';
import HrFieldUpload from '@/components/hr-field-upload';
import HrFieldSignature from '@/components/hr-field-signature';
import HrDocumentModuleInfo from '@/components/hr-document-module-info';
import router from '@/router';
import svc from '@/core/svc-client';
import t from '@/lib/i18next';
import {
  validate,
  validateOneWholeDocument,
} from '@/core/validation/validation';
import HrQuestionFactory from '@/components/hr-question-factory.vue';
import HrTfn from '@/components/hr-tfn.vue';
import HrAuRtw from '@/components/hr-au-rtw.vue';
import { cleanDocument } from '@/core/clean-document/clean-document';
import {
  DOCUMENT_NAMES,
  WORK_RIGHTS_UK_FIELDS,
  WORK_RIGHTS_AU_FIELDS,
} from '@/core/field-names';
import { getUpdatedDocumentFields } from '@/core/documents/fill-document';
import { COUNTRY } from '@/core/country';
import { getTypeValidator, typeTranslation } from '@/core/input-types/utils';
import { HR_ONBOARDING_STATES } from '../core/onboarding-states';
import { DOCUMENT_STATUSES } from '@/modules/definitions';
import { logWebHrError } from '@/core/logger/log-wrapper';
import ErrorBoundary from '@/components/error-boundary.vue';
import HrLoaderBlock from '@/components/hr-loader-block.vue';
import { shouldEnableLegalName } from '@/core/feature-flags';
import HrSuperApi from '@/components/hr-super-api.vue';

export default {
  components: {
    ErrorBoundary,
    HrFieldUpload,
    HrModule,
    HrSteps,
    HRField,
    HrButtonSave,
    HrModuleHeader,
    HrFieldSignature,
    HrQuestionFactory,
    HrTfn,
    HrSuperApi,
    HrAuRtw,
    HrDocumentModuleInfo,
    HrLoaderBlock,
  },
  props: {
    // mocking document to be used at dev module.
    document: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const state = reactive({
      currentDocument: {},
      saveState: 'disabled',
      showReviewButton: false,
      errorMessage: null,
      hasError: false,
      errorMessageArray: [],
      fieldValidation: {},
      signatureLoadingStates: new Map(),

      // reading router history requires to be reactive while current router doesn't
      // TODO: use useRoute instead after we upgrade library.
      router: router,
    });
    const onboardingId = computed(() => store.state.onboardingId);
    const isDocumentsComplete = computed(
      () =>
        store.getters.isDocumentsComplete &&
        !state.hasError &&
        state.saveState === 'saved'
    );
    const isDesktop = computed(() => store.state.isDesktop);
    const menuState = computed(() => store.state.menuState);
    const hasDocumentsError = computed(
      () => store.state.errorListInDocuments.length > 0
    );
    const mockedDocument = computed(() => {
      return JSON.parse(props.document);
    });

    const isAnySignatureLoading = computed(() => {
      return Array.from(state.signatureLoadingStates.values()).some(Boolean);
    });

    const isSuperApiDocument = computed(
      () =>
        store.getters.isSuperAPIEnabled &&
        getDocumentType(state.currentDocument) === DOCUMENT_NAMES.SUPER_API
    );

    // TODO: currently getters is not reactive. Test reactiveness without computed after upgrading library.
    const documentsFromStore = computed(() => store.getters.documents);
    const errorListInDocuments = computed(
      () => store.state.errorListInDocuments
    );
    const FIELD_TYPES = {
      SUPERFUND_AU: 'SUPERFUND_AU',
      ADDRESS_AU: 'ADDRESS_AU',
      FILE_UPLOAD: 'FILE_UPLOAD',
    };

    onUpdated(() => triggerFieldRules()); // TODO temporary solution to update the screen based on the rules
    onMounted(() => triggerFieldRules()); // TODO temporary solution to update the screen based on the rules

    watch(mockedDocument, (newVal) => {
      state.errorMessage = null;
      state.currentDocument = cloneObj(newVal);
      state.saveState = 'disabled';
    });

    watch(
      () => router.currentRoute.value.params.slug,
      () => {
        initialize();
      }
    );

    async function assignFieldValidation(field) {
      const type = await typeTranslation(field.type);
      if (type.validation) {
        state.fieldValidation = await getTypeValidator(type.validation);
      }
    }

    function triggerFieldRules() {
      return store.commit('Document/triggerFieldRules');
    }

    function menuToggle() {
      return store.commit('menuToggle');
    }

    function setDocument(newDocument) {
      return store.dispatch('setDocument', newDocument);
    }

    async function saveDocuments(newDocuments, documentToSave) {
      const payload = {
        ...newDocuments,
        currentDocumentName: documentToSave.name,
      };
      await store.dispatch('saveDocuments', payload);
    }

    function fieldFocus() {
      disableFormIfUploading();
    }

    async function fieldChange(index, val) {
      state.currentDocument.fields.splice(index, 1, val);
      updateFieldDependentFields(val);
      await setDocument(state.currentDocument);
      await nextTick();
      disableFormIfUploading();
      await validateDocumentDependentFields({
        changedField: state.currentDocument.fields[index],
      });

      if (
        state.currentDocument.name === DOCUMENT_NAMES.DOC_SIGNATURE_REQUESTS
      ) {
        await saveSignature(val);
      }
      if (state.currentDocument.name === DOCUMENT_NAMES.FILE_UPLOADS) {
        await save();
      }
    }

    async function saveSignature(val) {
      const {
        id: fieldId,
        docSignatureEmbedValue: {
          providerData: { signed: signedString },
        },
      } = val;

      state.currentDocument.status =
        state.currentDocument.completion < 100
          ? DOCUMENT_STATUSES.DOCUMENT_IN_PROGRESS
          : DOCUMENT_STATUSES.DOCUMENT_SUBMITTED;

      await store.dispatch('saveSignature', {
        fieldId,
        signed: signedString === 'true',
        documentId: state.currentDocument.id,
        currentDocument: state.currentDocument,
      });

      loadCurrentDocument();

      if (state.currentDocument.completion === 100) {
        menuToggle();
      }
    }

    function updateFieldDependentFields(val) {
      state.currentDocument.fields = getUpdatedDocumentFields(
        state.currentDocument,
        val
      );
    }

    async function updateUpload(index, val) {
      let fileUploadToBeSaved;
      if (val.requiredValues === undefined) {
        fileUploadToBeSaved = {
          ...val,
          fileValue: {
            ...val.fileValue,
          },
          value: {
            ...val.fileValue,
          },
        };
      } else {
        fileUploadToBeSaved = {
          ...val,
          fileValue: {
            ...val.fileValue,
            expiredAt:
              val.fileValue?.expiredAt &&
              new Date(val.fileValue.expiredAt)?.toISOString(),
          },
          value: {
            ...val.fileValue,
            expiredAt:
              val.fileValue.expiredAt &&
              new Date(val.fileValue.expiredAt)?.toISOString(),
          },
        };
      }

      if (
        [
          WORK_RIGHTS_UK_FIELDS.PASSPORT,
          WORK_RIGHTS_AU_FIELDS.PHOTO_ID,
        ].includes(val.name) &&
        Object.keys(val.fileValue || []).length === 0
      ) {
        fileUploadToBeSaved = {
          ...val,
          fileValue: {},
          value: {},
        };
      }

      await fieldChange(index, fileUploadToBeSaved);
    }

    async function review() {
      await save();
      if (!state.errorMessage) {
        await svc.onboarding.status(onboardingId.value, 'REVIEW');
        store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.REVIEW);
        await router.push({ name: 'view-module' });
      }
    }

    async function validateFieldsBeforeSave() {
      let canSave = true;
      state.currentDocument.fields.forEach((field) => {
        if (field.validation && field.validation.validator) {
          field.isInvalid = !field.validation.validator(field.value, true);

          if (field.isInvalid) {
            canSave = false;
          }
        }
      });

      return canSave;
    }

    /**
     * This is where you put validation where validation of single field
     * requires another document's value.
     * @returns {Promise<Boolean>}
     */
    async function validateDocumentDependentFields({
      changedField = null,
      docToSave = state.currentDocument,
    }) {
      const canSave = await validateOneWholeDocument(docToSave, changedField);
      await setDocument(docToSave);

      return canSave;
    }

    /**
     * This is where you put validations that fields are independent of other
     * field's value.
     * @returns {Promise<Boolean>}
     */
    async function validateEachFields(documentToSave = state.currentDocument) {
      let hasError = false;

      for (const field of documentToSave.fields) {
        await validate(field);
        if (field?.fieldError) {
          hasError = true;
        }
        if (
          (field.type === FIELD_TYPES.SUPERFUND_AU ||
            field.type === FIELD_TYPES.ADDRESS_AU) &&
          !hasError
        ) {
          await assignFieldValidation(field);
          field.validation = { ...field.validation, ...state.fieldValidation };
        }

        const birthCertificate = documentToSave.fields.find(
          (field) => field.name === WORK_RIGHTS_UK_FIELDS.BIRTH_CERTIFICATE
        );

        if (
          birthCertificate?.fileValue?.sizeInBytes > 0 &&
          [
            WORK_RIGHTS_UK_FIELDS.PASSPORT,
            WORK_RIGHTS_AU_FIELDS.PHOTO_ID,
          ].includes(field.name) &&
          !field.fileValue?.sizeInBytes
        ) {
          field.requiredValues.expiredAt = true;
          field.dateError = '';
        }

        if (
          (birthCertificate?.fileValue?.sizeInBytes === 0 &&
            [
              WORK_RIGHTS_UK_FIELDS.PASSPORT,
              WORK_RIGHTS_AU_FIELDS.PHOTO_ID,
            ].includes(field.name)) ||
          (birthCertificate?.fileValue?.sizeInBytes > 0 &&
            [
              WORK_RIGHTS_UK_FIELDS.PASSPORT,
              WORK_RIGHTS_AU_FIELDS.PHOTO_ID,
            ].includes(field.name) &&
            field.fileValue?.sizeInBytes > 0 &&
            !field.fileValue?.expiredAt)
        ) {
          field.dateError = validateDate(field);
          field.requiredValues.expiredAt = false;
        }

        if (field.type === FIELD_TYPES.FILE_UPLOAD) {
          if (
            field.hasExpiry &&
            field.name !== WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS &&
            !birthCertificate?.fileValue?.sizeInBytes
          ) {
            field.dateError = validateDate(field);
          }
        }
      }

      await setDocument(documentToSave);
      return !hasError;
    }

    function validateDate(field) {
      const expiredAt = field?.fileValue?.expiredAt;
      if (!expiredAt) {
        return t('hr-field-upload:required-expiry');
      }
      if (new Date(expiredAt) < new Date()) {
        return t('hr-field-upload:required-expiry');
      }
    }

    async function autoSave({ currentStep }) {
      // This is to experimentally removing auto save to observe
      // since we are not confident with the change yet.
      if (shouldEnableLegalName()) {
        return;
      }

      const documentToSave = state.currentDocument;

      try {
        if (!currentStep) {
          return;
        }
        await cleanUpDocumentBeforeSave();

        await validateAll(documentToSave);

        await saveDocuments(
          {
            id: onboardingId.value,
            documents: documentsFromStore.value,
          },
          documentToSave
        );

        store.commit('removeDocumentBackendErrors', documentToSave);
      } catch (e) {
        logWebHrError({
          message: 'Error in autosave',
          source: 'document-module',
          metadata: {
            tabName: documentToSave.name,
            currentTab: currentStep,
          },
          error: e,
        });

        store.commit('setDocumentBackendErrors', {
          error: e,
          currentDocument: documentToSave,
        });
      }
    }

    async function validateAll(documentToSave = state.currentDocument) {
      const eachFieldValidationResult = await validateEachFields(
        documentToSave
      );
      const dependentFieldValidationResult =
        await validateDocumentDependentFields({ docToSave: documentToSave });
      return {
        eachFieldValidationResult,
        dependentFieldValidationResult,
      };
    }

    async function cleanUpDocumentBeforeSave() {
      state.currentDocument = cleanDocument(state.currentDocument);
      await setDocument(state.currentDocument);
    }

    async function reviewAndSubmit() {
      try {
        await save();
      } catch (e) {
        logWebHrError({
          message: 'failed to save document before review step',
          source: 'document-module',
          metadata: {
            documentErrorsLength: store.state.documentErrors.length,
            tabName: state.currentDocument.name,
          },
          error: e,
        });
      }

      try {
        // TODO: investigate why this is not within same closure as await save call
        if (!store.state.documentErrors?.length) {
          await svc.onboarding.status(store.state.onboarding.id, 'REVIEW');
          store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.REVIEW);
          await router.push({ name: 'view-module' });
        }
      } catch (e) {
        logWebHrError({
          message: 'failed to update status to review',
          source: 'document-module',
          metadata: {
            documentErrorsLength: store.state.documentErrors.length,
            tabName: state.currentDocument.name,
          },
          error: e,
        });
      }
    }

    function handleBackendErrors(e) {
      const documentToSave = state.currentDocument;

      logWebHrError({
        message: 'failed to save document',
        source: 'document-module',
        metadata: {
          savingState: state.saveState,
          documentErrorsLength: store.state.documentErrors.length,
          tabName: documentToSave.name,
        },
        error: e,
      });

      loadCurrentErrorMessages();

      store.commit('setDocumentBackendErrors', {
        error: e,
        currentDocument: documentToSave,
      });

      if (e?.code === 401) {
        state.errorMessageArray = [
          'Your session has expired. Please reopen the link again',
        ];
      }
      if (e) {
        state.errorMessage = state.errorMessageArray.join(' ');
      } else {
        // This is needed because previously `e` itself was undefined
        // because we were throwing error with `e.response` when the error
        // is Network error. When that happens, this will be needed to address
        // and shows a generic error message to the user.
        state.errorMessage = 'An error has occurred. Please try again later';
      }

      state.saveState = 'disabled';
    }

    async function save() {
      const documentToSave = state.currentDocument;

      try {
        state.errorMessage = null;
        if (state.saveState === 'saved') {
          return true;
        }
        await cleanUpDocumentBeforeSave();

        const { eachFieldValidationResult, dependentFieldValidationResult } =
          await validateAll();

        if (!eachFieldValidationResult || !dependentFieldValidationResult) {
          state.hasError = true;
          state.saveState = null;
          return;
        }

        state.saveState = 'saving';
        await saveDocuments(
          {
            id: onboardingId.value,
            documents: documentsFromStore.value,
          },
          documentToSave
        );

        store.commit('removeDocumentBackendErrors', documentToSave);

        const currentStatus = store.getters.onboardingStatus;

        // Only update status when there current status is different than intended status
        if (
          !isDocumentsComplete.value &&
          currentStatus !== HR_ONBOARDING_STATES.STARTED
        ) {
          svc.onboarding.status(onboardingId.value, 'STARTED');
          store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.STARTED);
        }
        if (store.getters.isDocumentsComplete && hasDocumentsError.value) {
          throw new Error(
            'Some documents in other tabs were invalid. Please have a look into other documents'
          );
        }
        state.errorMessageArray = [];
        state.hasError = false;
        state.saveState = 'saved';

        // TODO: count numbers of completion and compare this showReviewButton state
        state.showReviewButton = isDocumentsComplete.value;
        menuToggle();
      } catch (e) {
        state.hasError = true;
        handleBackendErrors(e);
      }
    }

    function disableFormIfUploading() {
      if (state.currentDocument.completion >= 100) {
        state.saveState = null;
      } else {
        state.saveState = 'disabled';
      }
    }

    function loadCurrentDocument() {
      state.currentDocument = cloneObj(
        documentsFromStore.value.find(
          (doc) => doc.slug === state.router.currentRoute.params.slug
        ) || {}
      );
    }

    function loadCurrentErrorMessages() {
      store.commit('loadRelatedBackendErrors', state.currentDocument.name);
      state.errorMessageArray = errorListInDocuments.value;
    }

    async function initialize() {
      if (!mockedDocument.value && !documentsFromStore.value) {
        return router.push({ name: 'OnboardingModule' });
      }
      if (!mockedDocument.value && documentsFromStore.value) {
        loadCurrentDocument();

        if (Object.keys(state.currentDocument).length === 0) {
          return router.push({ name: 'OnboardingModule' });
        }

        return true;
      }

      loadCurrentDocument();
      loadCurrentErrorMessages();
    }

    function getDocumentType(field) {
      return field?.name?.includes('.')
        ? field.name.substring(0, field.name.indexOf('.'))
        : field?.name;
    }

    function handleSignatureLoadingStatus(fieldIndex, status) {
      state.signatureLoadingStates.set(fieldIndex, status);
    }

    initialize();

    return {
      t,
      state,
      isDocumentsComplete,
      onboardingId,
      isDesktop,
      menuState,
      triggerFieldRules,
      menuToggle,
      fieldFocus,
      fieldChange,
      review,
      validateFieldsBeforeSave,
      save,
      disableFormIfUploading,
      autoSave,
      updateUpload,
      DOCUMENT_NAMES,
      COUNTRY,
      getDocumentType,
      reviewAndSubmit,
      isAnySignatureLoading,
      handleSignatureLoadingStatus,
      store,
      isSuperApiDocument,
    };
  },
};
</script>

<style scoped lang="scss">
.document-module .hr-button-save {
  margin: 40px 0 80px 0;
}

.hasFooter {
  margin-bottom: 100px;
}

.document-module__field {
  margin-bottom: 32px;
}

.signature-loading-overlay {
  position: relative;
  height: 200px;
  margin-bottom: 32px;
}
</style>
